import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Eye, Heart, Users } from "lucide-react";

const IMAGES = {
  about: "/images/about.jpg",
  mission: "/images/mission.jpg",
};

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="noise-overlay">
      <Helmet>
        <title>About Prithira Global | Sustainable Packaging from India</title>
        <meta name="description" content="Learn about Prithira Global — an India-based eco-conscious B2B supplier of biodegradable food packaging. Our mission, vision, and ethical sourcing story." />
        <meta name="keywords" content="about prithira global, sustainable packaging company India, eco friendly supplier story, biodegradable exporter mission" />
        <link rel="canonical" href="https://www.prithiraglobal.com/about" />
        <meta property="og:title" content="About Prithira Global | Sustainable Packaging from India" />
        <meta property="og:description" content="India-based eco-conscious B2B supplier of biodegradable food packaging. Our mission, vision, and ethical sourcing story." />
        <meta property="og:url" content="https://www.prithiraglobal.com/about" />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Our Story</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Rooted in <span className="italic">Purpose</span>, Growing Globally
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              From India's agricultural heartland, we're transforming natural
              fibers into responsible packaging solutions for the world.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden bg-stone-50">
                <img
                  src={IMAGES.about}
                  alt="From nature, back to nature — biodegradable tableware"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            <div>
              <h2 className="heading-lg text-stone-900 mb-6">
                Our <span className="italic">Beginning</span>
              </h2>
              <div className="space-y-6 text-stone-600 font-light leading-relaxed">
                <p>
                  Prithira Global was born from a simple observation: India's
                  agricultural abundance offers an incredible opportunity to create
                  sustainable alternatives to plastic packaging.
                </p>
                <p>
                  Our journey began in the sugarcane fields of India, where we saw
                  bagasse — the fibrous residue left after juice extraction — being
                  burned as waste. We saw potential where others saw disposal.
                </p>
                <p>
                  Today, we partner with farming communities across India,
                  transforming agricultural byproducts into premium biodegradable
                  tableware that serves businesses worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="section-spacing bg-sand-200/30">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Our Mission",
                content:
                  "To reduce global plastic dependency by providing premium, sustainable packaging solutions that businesses can trust.",
              },
              {
                icon: Eye,
                title: "Our Vision",
                content:
                  "A world where every meal is served on materials that return safely to the earth, leaving no trace but nourishment.",
              },
              {
                icon: Heart,
                title: "Our Values",
                content:
                  "Sustainability, quality, transparency, and partnership. We believe in building lasting relationships based on trust.",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`about-card-${index}`}
                className="bg-white rounded-2xl p-8 border border-stone-100"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <item.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl text-stone-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-stone-600 font-light text-sm leading-relaxed">
                  {item.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-primary mb-4 block">Our Partners</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              Who We <span className="italic">Serve</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              "HoReCa",
              "Institutions",
              "Distributors",
              "Importers",
              "Food Chains",
              "Retailers",
            ].map((partner, index) => (
              <div
                key={index}
                className="bg-sand-200/50 rounded-xl p-6 text-center hover:bg-sand-200 transition-colors"
              >
                <Users className="h-8 w-8 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <span className="text-sm font-medium text-stone-700">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="section-spacing bg-stone-900 text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-terra-400 mb-4 block">Quality First</span>
              <h2 className="heading-lg mb-6">
                Commitment to <span className="italic">Excellence</span>
              </h2>
              <p className="text-stone-400 font-light leading-relaxed mb-8">
                Every product that leaves our facility meets rigorous quality
                standards. We work with certified production partners who share
                our commitment to sustainability and excellence.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "ISO-certified production facilities",
                  "Food-safe materials and processes",
                  "Consistent quality across batches",
                  "Comprehensive testing protocols",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-stone-300">
                    <div className="w-2 h-2 rounded-full bg-terra-500" />
                    <span className="font-light">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/partner">
                <Button
                  data-testid="about-partner-btn"
                  className="rounded-full px-8 py-6 text-base font-medium bg-white text-stone-900 hover:bg-stone-100 btn-lift"
                >
                  Partner With Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={IMAGES.mission}
                  alt="Quality commitment"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Export Credentials */}
      <section
        data-testid="about-export-credentials"
        className="py-16 bg-sand-200/40"
      >
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-white border border-stone-100 rounded-2xl p-8 md:p-10 shadow-sm">
            <span className="caption text-primary mb-3 block">Compliance</span>
            <h2 className="font-heading text-2xl md:text-3xl text-stone-900 mb-6">
              Export <span className="italic">Credentials</span>
            </h2>
            <dl className="space-y-4 text-stone-700">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                <dt className="text-sm font-medium text-stone-900 sm:w-56 shrink-0">
                  Import Export Code (IEC)
                </dt>
                <dd className="text-sm font-light tracking-wide">AQAPR7398L</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                <dt className="text-sm font-medium text-stone-900 sm:w-56 shrink-0">
                  Issued by
                </dt>
                <dd className="text-sm font-light leading-relaxed">
                  Directorate General of Foreign Trade,
                  <br className="hidden sm:block" />
                  {" "}Ministry of Commerce &amp; Industry, Government of India
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
